/**
 * `Consolidated Daily UM Debrief Report · A4` — the issued document (um-app spec 07, spec 13 §6).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 74 — THE PER-MATCH RESTRUCTURE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Section 02 is new and is the document's spine: **one block per match, in chronological order**,
 * carrying the match line, its U1/U2/VU with role codes, each umpire's Call 6 summary, the
 * released observations behind it under `Show Notes ▾`, and spec 13 §8's `What The Umpire Said`.
 * The day-level sections that follow are unchanged.
 *
 * **Spec 07 §3's one-page rule is dropped**, and its overflow policy with it — both truncated
 * exactly the material this redesign exists to surface. In its place: clean multi-page print with
 * **each match section kept whole**, which is a `break-inside: avoid` on `.a4match` in
 * `editorial.css`, and every `<details>` on this page **forced open in print** by the same block,
 * so a sheet handed round a room carries its evidence rather than a row of closed triangles.
 *
 * **Ruling R-D5.** A response released after the report was built prints under one stamped strip,
 * `Debrief Held After This Report Was Built · Added hh:mm`. The API decides which responses those
 * are; this page renders the marking and computes none of it.
 *
 * **Ruling R-D6.** The footer promise no longer names 21:30 — the report builds about thirty
 * minutes after the last match of the day ends, and a printed promise must be literally true of
 * the generator.
 *
 * Authority: `um-app/13-match-debrief.md` §6 and §8;
 * `um-app/07-debrief-pack-document.md` §2 (the structure), §3 (the layout rules),
 * §4 (what may be read) and §5 (the states); `um-app/11-release-and-debrief.md` §4.2–§4.3 for
 * where the pack comes from and what a regeneration stamp means.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHAPE: A PRINT-STYLED PAGE, NOT A SERVER-GENERATED FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §3 asks for the document to be *"rendered to PDF server-side at generation time and stored
 * with the pack record"*. **That is not what this is, and the difference is stated rather than
 * papered over.** This is the pack laid out as one A4 page and printed by the reader's own
 * browser — the same path `06-hearings.md` §10 takes for the hearing letters, over the
 * `@media print` block that has been in `editorial.css` since that run. Producing a stored PDF
 * would mean a PDF engine in the API and a blob to keep it in, and a document byte-identical
 * across readers is a claim only a stored file can make. Nothing here makes that claim.
 *
 * What this DOES keep of §3 is the part that is about the paper: A4 portrait, the letterhead,
 * blocks that are not split across a page turn, and colour that is never the only channel.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SOURCE, AND IT IS THE ONE THE PHONE READS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every figure, sentence and flag on this page comes from `GET /um/debrief` — the same call,
 * the same day parameter and the same body `UmDebriefPage` renders. **Nothing is recomputed
 * here and nothing is composed here.** A document that worked out its own summary line would
 * be a second source of truth for the same matchday, and the two would drift on the first day
 * somebody changed one of them. Where the document needs a word the pack does not carry, the
 * word is absent — see the omissions below.
 *
 * That also settles the privacy question by construction: the route serves released notes only
 * (`debrief/assembly.ts`), so an unreleased note has no path onto this page. Author attribution
 * is whatever the pack carries, which is `N UMs` on a duty row and the author's short name on
 * an `Also Raised` note — exactly what the phone shows, and no more.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT SPEC 07 ASKS FOR THAT IS ABSENT, AND WHY EACH ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * **§2.1's `Page 1 Of 1`.** The browser paginates, and this page does not measure itself.
 *    A hard-coded `Page 1 Of 1` on a document that ran to two sheets would be a printed lie.
 *  * **§2.2's `31 Released Notes` as a tournament-day total.** The pack carries no day total:
 *    a duty row's `noteCount` covers the umpires on the roster, and on an `appointed` day a
 *    released note about somebody not appointed is in neither the rows nor `Also Raised`. What
 *    is printed instead is the count of the notes ON THIS PAGE, labelled as that. §2.2's rule
 *    that the figure is never broken down by author is kept either way.
 *  * **§2.3's pool, and the umpires appointed to each of TODAY's fixtures.** The pack's
 *    `fixtures` carry the match number, competition, pitch and time and nothing else; only
 *    `tomorrow.fixtures` carry an `onField` list. Today's umpires are named in section 02,
 *    where the evidence about them is, rather than invented onto section 01's rows.
 *  * **§2.4's contributor chips (`Notes From · UM · Naoko …`).** The pack carries
 *    `distinctAuthorCount` and no author names on a duty row, deliberately — the phone shows
 *    `4 Notes From 2 UMs` and this shows the same sentence. Naming the contributors here would
 *    be a per-author disclosure that the surface it is printed from does not make.
 *  * **§2.8's `AG2026-HKY` event code.** The tournament record has a name, an edition, a host
 *    and a zone; it has no short code. The document reference prints the half that exists.
 *  * ~~**§3's one-page fit and its overflow policy**~~ — **struck by spec 13 §6 (run 74)**, and
 *    it was already absent here for the reason the amendment gives: all three DELETE evidence to
 *    make a page fit, and doing that silently to a document that is the room's agenda is worse
 *    than a second sheet. The print rules keep each match whole and let the paper run on.
 *  * **§3's repeated letterhead on a second page.** It needs the document to be a table for
 *    the browser to repeat a header, and rebuilding four sections as table rows to get it
 *    would cost the layout for a case that is rare.
 *  * **§5's `Regenerate` control.** TD-only (spec 11 §4.3), and this is the managers' screen —
 *    the same reason `UmDebriefPage` gives for not drawing it either. The stamp it produces IS
 *    printed, because the pack carries it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROMISE IN THE FOOTER IS BRANCHED, BECAUSE IT IS NOT ALWAYS TRUE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2.8's footer sentence is *"a promise statement printed on every copy [that] must be literally
 * true of the generator"*. It is true of a **stored** artefact, which the nightly job assembled.
 * It is not true of the computed-on-open fallback, which nothing assembled to a schedule. So the
 * clause about the schedule is printed only on the artefact it is true of, and the
 * released-notes-only half — which is true of both, and is the half a UM would want to check —
 * is printed on every copy.
 *
 * **Amended run 74 (R-D6):** the clause says *"after the last match of the day"* rather than
 * *"at 21:30"*, because that is when the build now happens.
 *
 * The provenance band follows the same rule as `UmDebriefPage`'s duty kicker: it says
 * `AI-Assembled` only when `ai.state` is `present`, and `Assembled` in every other state.
 */

import { Link, useSearchParams } from "react-router-dom";

import { fetchDebriefPack } from "../../api/client.js";
import type {
  DebriefFixture,
  DebriefFlag,
  DebriefGeneralNote,
  DebriefMatchResponse,
  DebriefMatchSection,
  DebriefMatchUmpire,
  DebriefPackBody,
  DebriefTomorrowFixture,
  DebriefUmpireRow,
} from "../../api/types.js";
/* The one press this system has ever had for putting a document on paper — `window.print()`,
   guarded for jsdom. It is imported from the hearings feature rather than copied because there
   must be exactly one of it: a second copy would be a second thing to fix the day the guard
   needs changing. */
import { printDocument } from "../../features/hearings/documentText.js";
import { useTournament } from "../../app/session.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
/* The pack's provenance kicker, read off the phone's own module rather than reimplemented:
   `Assembled hh:mm`, `Regenerated hh:mm` and `Computed On Open` must read identically on the
   paper and on the screen, and two copies of that rule would eventually disagree. */
import { debriefKicker } from "./UmDebriefPage.js";
/* Ruling R-D5's strip, spec 13 §8's heading, the Match Debrief route and the alignment split —
   all four are the vocabulary module's, so the paper and the phone cannot word them apart. */
import {
  addendumStripLine,
  matchDebriefPath,
  matchSectionAnchor,
  RESPONSES_HEADING,
  splitSummary,
} from "./matchDebrief.js";

/**
 * `Monday 21 September 2026` — §2.1's meta line, which carries the year the phone's kicker
 * does not. A document that outlives the tournament needs to say which one it is from.
 *
 * Rendered in UTC over a bare `YYYY-MM-DD`, exactly as `tokyoDateKicker` is: the value is a
 * calendar date and not an instant, and any zone offset applied to it would move it a day.
 */
export function documentDateLine(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
    .format(parsed)
    /* `en-GB` puts a comma after the weekday once a year is asked for and not before. The
       comma is dropped so this line reads the way `tokyoDateKicker` reads on the phone and
       the way §2.1 writes it — `Monday 21 September 2026`. */
    .replace(",", "");
}

/**
 * `Matchday 4`, or null on a day the schedule does not know.
 *
 * The number is the day's position in the tournament's list of days WITH FIXTURES, computed
 * the same way `matchdayLabel` computes it on the phone — a rest day is not a matchday, and
 * counting calendar dates would make it one. Null rather than a guess: §2.8's document
 * reference would otherwise carry a matchday number for a day that has none.
 */
export function matchdayNumber(days: readonly string[], day: string | null): number | null {
  if (day === null) return null;
  const index = days.indexOf(day);
  return index < 0 ? null : index + 1;
}

/** §2.8's document reference, with the event code the tournament record has no field for. */
export function documentReference(matchday: number | null): string {
  return matchday === null ? "UM-DEBRIEF" : `UM-DEBRIEF · MD${String(matchday)}`;
}

/**
 * §2.2's provenance band sentence. `AI-Assembled` is a claim about a model, so it is made only
 * in the one state where a model wrote every line on the page.
 */
export function provenanceLine(aiState: string): string {
  const how = aiState === "present" ? "AI-Assembled" : "Assembled";
  return `${how} From Released Notes · Nothing Here Left The UM Team`;
}

/**
 * §2.2's second pill. **Not §2.2's figure** — see the omissions in the header: this counts the
 * released notes that are ON this page, which is what the pack can support, and says so.
 */
export function notesOnPageCount(pack: DebriefPackBody): number {
  const onDuty = pack.umpiresOnDuty.rows.reduce((total, row) => total + row.noteCount, 0);
  return onDuty + pack.alsoRaised.length;
}

/** `N Released Notes On This Page`, singular when it is one. */
export function notesOnPagePill(count: number): string {
  return count === 1 ? "1 Released Note On This Page" : `${count} Released Notes On This Page`;
}

/** §2.5's per-flag kicker, from the kind the API computed. Never from the words. */
export function flagKicker(kind: DebriefFlag["kind"]): string {
  if (kind === "coverage") return "Coverage";
  return kind === "alignment" ? "Alignment" : "Positive Trend";
}

function competitionClass(gender: "m" | "w"): string {
  return gender === "m" ? "men" : "women";
}

function competitionName(gender: "m" | "w"): string {
  return gender === "m" ? "Men's" : "Women's";
}

/**
 * §2.3/§2.6's `.fxline`. Pitch and the on-field pair are drawn only when the pack carries
 * them — today's fixtures carry no appointments and some fixtures carry no pitch, and a row
 * that printed `—` for both would look like a fixture with neither.
 */
function FixtureLine({
  fixture,
  onField,
}: {
  fixture: DebriefFixture;
  onField: readonly string[];
}): React.JSX.Element {
  return (
    <li className="fxline" data-testid={`fx-${String(fixture.tmsMatchDbId)}`}>
      {/* Colour is never the only channel (gap A4, and §3: this is printed in monochrome) —
          the M/W letter is in the id itself and the competition is spelled out beside it. */}
      <span className={`fxline__id fxline__id--${competitionClass(fixture.competitionGender)}`}>
        {fixture.displayMatchNo}
      </span>
      <span className="fxline__comp">{competitionName(fixture.competitionGender)}</span>
      <span className="fxline__who">{onField.join(" · ")}</span>
      <span className="fxline__where num">{fixture.pitch ?? ""}</span>
      <span className="fxline__when num">{fixture.time}</span>
    </li>
  );
}

/**
 * §2.4's `.ublk`.
 *
 * Two lines of words and no more: the AI sentence when the pack carries one, and ALWAYS the
 * deterministic summary beneath it — the same two-layer choice `UmDebriefPage`'s duty row
 * makes, for the same reason. A panel reading a sentence aloud in the room is entitled to see
 * the count it was made from on the same sheet.
 */
function UmpireBlock({ row }: { row: DebriefUmpireRow }): React.JSX.Element {
  return (
    <li className="ublk" data-testid={`ublk-${row.tmsPersonId}`}>
      <span className="ublk__avatar" aria-hidden="true">
        {row.initials}
      </span>
      <span className="ublk__body">
        <span className="ublk__head">
          <b className="ublk__nm">{row.mediaName}</b>
          <span className="ublk__meta num">
            {row.country}
            {row.slots.length === 0 ? "" : ` · ${row.slots.join(" · ")}`}
          </span>
          <span className="ublk__count num">
            {row.noteCount === 1 ? "1 Note" : `${row.noteCount} Notes`}
          </span>
        </span>
        {row.aiLine === null ? null : (
          <span className="ublk__ai" data-testid={`ublk-ai-${row.tmsPersonId}`}>
            {row.aiLine}
          </span>
        )}
        <span className="ublk__sum num">{row.summary}</span>
      </span>
    </li>
  );
}

/**
 * §2.5's `.a4flag`. The glyph and the kicker both say the kind, so a monochrome print loses
 * nothing; the words are the API's, and which of `aiDetail`/`detail` is drawn follows
 * `UmDebriefPage`'s rule exactly — never both, because a flag's two paragraphs say the same
 * thing twice.
 */
function FlagBlock({ flag }: { flag: DebriefFlag }): React.JSX.Element {
  return (
    <li className={`a4flag a4flag--${flag.kind}`} data-testid={`a4flag-${flag.id}`}>
      <span className="a4flag__glyph" aria-hidden="true">
        {flag.glyph}
      </span>
      <span className="a4flag__body">
        <span className="a4flag__kicker">{flagKicker(flag.kind)}</span>
        <b className="a4flag__title">{flag.title}</b>
        <span className="a4flag__detail">{flag.aiDetail ?? flag.detail}</span>
      </span>
    </li>
  );
}

function AlsoRaisedBlock({ note }: { note: DebriefGeneralNote }): React.JSX.Element {
  return (
    <li className="a4gen" data-testid={`a4also-${note.id}`}>
      {/* The author's short display name — the attribution the phone makes on this section
          and nowhere else. Never a country code, never a note total per author. */}
      <span className="a4gen__who num">
        UM · {note.authorShortName ?? "Unattributed"}
        {note.matchNo === null ? "" : ` · ${note.matchNo}`}
      </span>
      <span className="a4gen__body">{note.bodyEn}</span>
    </li>
  );
}

/* ── Run 74 — spec 13 §6's per-match sections, on paper ────────────────────── */

/**
 * One umpire inside a match section: seat, organised summary, and the notes behind it.
 *
 * **Nothing is recomposed here.** The summary is Call 6's, served on the pack; the alignment
 * sentence is split off with `splitSummary`, the same function the phone and the Match Debrief
 * screen use. Both halves print: this is the panel's own agenda and there is no Room View on
 * paper handed round a manager's meeting.
 */
function A4MatchUmpire({ umpire }: { umpire: DebriefMatchUmpire }): React.JSX.Element {
  const split =
    umpire.summary === null ? null : splitSummary(umpire.summary, umpire.hasAlignment);

  return (
    <div className="a4ump" data-testid={`a4-umpire-${umpire.tmsPersonId}`}>
      <span className="a4ump__head">
        <b className="ublk__nm">{umpire.mediaName}</b>
        {/* The role code on every block, beside the name. */}
        <span className="ublk__meta num">
          {`${umpire.country} · ${umpire.slot} · ${umpire.slotLabel}`}
        </span>
        <span className="ublk__count num">
          {umpire.noteCount === 1 ? "1 Note" : `${String(umpire.noteCount)} Notes`}
        </span>
      </span>

      {split === null ? (
        <span className="a4empty">{umpire.notice ?? "No summary was produced."}</span>
      ) : (
        <>
          <span className="a4sum__kicker">AI Summary</span>
          {split.body.map((paragraph, index) => (
            <span className="ublk__ai" key={index}>
              {paragraph}
            </span>
          ))}
          {split.alignment === null ? null : (
            <>
              <span className="a4sum__kicker">For The Managers</span>
              <span className="ublk__ai">{split.alignment}</span>
            </>
          )}
        </>
      )}

      {/* Closed on screen, FORCED OPEN in print by `editorial.css` — a document taken into a
          room must carry the evidence its summaries were made from. */}
      <details className="mddisc" data-testid={`a4-notes-${umpire.tmsPersonId}`}>
        <summary className="mddisc__sum">
          <span className="card__kicker">Show Notes ▾</span>
        </summary>
        <div className="mddisc__body">
          {umpire.observations.length === 0 ? (
            <span className="a4empty">Nothing has been released about this umpire yet.</span>
          ) : (
            <ul className="mdnotes">
              {umpire.observations.map((note) => (
                <li className="mdnote" key={note.id}>
                  <span className="mdnote__crit">{note.criterion}</span>
                  <span className="mdnote__when num">{note.when ?? "Unstamped"}</span>
                  <span className="mdnote__au num">
                    UM · {note.authorShortName ?? "Unattributed"}
                  </span>
                  <span className="mdnote__txt">{note.bodyEn}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}

/**
 * One match, kept whole across a page turn — spec 13 §6, and the replacement for spec 07 §3's
 * one-page rule.
 *
 * The header links down to that match's Match Debrief screen. On paper the link is inert ink,
 * which is the honest state of every link in a printed document; on screen it is the way in.
 */
function A4MatchSection({ section }: { section: DebriefMatchSection }): React.JSX.Element {
  const core = section.responses.filter((response) => !response.addedAfterBuild);
  const addenda = section.responses.filter((response) => response.addedAfterBuild);

  const responseRow = (response: DebriefMatchResponse): React.JSX.Element => (
    <li className="mdnote" key={response.id}>
      <span className="mdnote__au num">
        {`${response.mediaName}${response.slot === null ? "" : ` · ${response.slot}`}`}
        {` · Recorded By UM · ${response.authorShortName ?? "Unattributed"}`}
      </span>
      <span className="mdnote__txt">{response.bodyEn}</span>
    </li>
  );

  return (
    <section
      className="a4match"
      id={matchSectionAnchor(section.displayMatchNo)}
      data-testid={`a4-match-${section.displayMatchNo}`}
    >
      <h4 className="a4match__head">
        {/* Colour is never the only channel (gap A4) — this is printed in monochrome. */}
        <span
          className={`fxline__id fxline__id--${competitionClass(section.competitionGender)}`}
        >
          {section.displayMatchNo}
        </span>
        <span className="a4match__who">
          {section.teams ?? "Teams Not Yet Known"}
          {section.score === null ? null : (
            <span className="a4match__score num">{section.score}</span>
          )}
        </span>
        <span className="a4match__when num">
          {[section.pitch, section.time].filter((part) => part !== null).join(" · ")}
        </span>
        <Link
          className="a4match__go"
          to={matchDebriefPath(section.tmsMatchDbId, section.competitionId)}
        >
          Match Debrief ›
        </Link>
      </h4>

      {section.umpires.length === 0 ? (
        <span className="a4empty">
          No umpire appointment for this match is held in this system, so there is nobody to
          summarise.
        </span>
      ) : (
        section.umpires.map((umpire) => (
          <A4MatchUmpire key={umpire.tmsPersonId} umpire={umpire} />
        ))
      )}

      <div className="a4said">
        <span className="a4sum__kicker">{RESPONSES_HEADING}</span>
        {section.responses.length === 0 ? (
          <span className="a4empty">
            Nothing has been written down from this match&rsquo;s debrief yet.
          </span>
        ) : (
          <>
            {core.length === 0 ? null : <ul className="mdnotes">{core.map(responseRow)}</ul>}
            {addenda.length === 0 ? null : (
              <>
                {/* RULING R-D5 — the live half of a frozen artefact, said once and stamped. */}
                <span
                  className="a4add"
                  data-testid={`a4-addendum-${section.displayMatchNo}`}
                >
                  {addendumStripLine(addenda[0]?.addedAt ?? null)}
                </span>
                <ul className="mdnotes">{addenda.map(responseRow)}</ul>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** §2's section frame: a numbered heading on the left and §2's rubric on the right. */
function DocSection({
  number,
  title,
  rubric,
  children,
}: {
  number: string;
  title: string;
  rubric: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="a4sect" data-testid={`a4sect-${number}`}>
      <h3 className="a4sect__head">
        <span className="a4sect__no num">{number}</span>
        <span className="a4sect__title">{title}</span>
        <span className="a4sect__rubric">{rubric}</span>
      </h3>
      {children}
    </section>
  );
}

export function UmDebriefDocumentPage(): React.JSX.Element {
  const [params] = useSearchParams();
  /**
   * The day comes off the URL so the document is a link somebody can keep, re-open and hand
   * to the next reader. No `?day=` means the same thing it means on the API: whichever day
   * the server picks — the most recent matchday at or before today.
   */
  const day = params.get("day");
  const pack = useAsync(() => fetchDebriefPack(day ?? undefined), [day]);
  const tournament = useTournament();

  const body: DebriefPackBody | null = pack.status === "ready" ? pack.data : null;

  return (
    <>
      {/*
       * The desk furniture, outside the paper: it is hidden in print by `editorial.css`'s
       * existing `@media print` rule, which makes everything but `.printdoc` invisible.
       *
       * `Print` rather than `Download`: this system has no file writer, and a button labelled
       * `Download` would promise one. The sentence beside it says what the press does, in the
       * same register `06-hearings.md`'s output note uses.
       */}
      <div className="a4bar" data-testid="a4bar">
        <Link className="a4bar__back" to={`${UM_ROOT_PATH}/debrief`}>
          ← Back To The Pack
        </Link>
        <button type="button" className="a4bar__print" onClick={printDocument}>
          Print
        </button>
        <span className="a4bar__note">
          This day&rsquo;s report, from released notes only, over as many pages as it needs —
          each match kept whole. Your browser&rsquo;s own print dialogue puts it on paper or
          saves it on your device. Nothing is sent from here.
        </span>
      </div>

      {pack.status === "loading" ? <Skeleton rows={6} /> : null}
      {pack.status === "error" ? <FailBanner error={pack.error} /> : null}

      {body === null ? null : (
        <article className="a4doc printdoc" data-testid="a4doc">
          {/* §2.1's letterhead. The event names itself from the tournament record (M1) and
              never from a constant here; an installation with no tournament set up prints no
              event line at all, exactly as the two mastheads do. */}
          <header className="a4__lh">
            <div className="a4__lhleft">
              <span className="a4__kicker">
                {tournament === null
                  ? "Umpires Manager Panel"
                  : tournament.fullNameForLetterheads}
              </span>
              <h2 className="a4__title">
                Consolidated Daily UM Debrief Report
                {matchdayNumber(body.days, body.day) === null ? null : (
                  <span className="a4__titlesuffix">
                    {" · "}
                    Matchday {matchdayNumber(body.days, body.day)}
                  </span>
                )}
              </h2>
            </div>
            <div className="a4__lhmeta">
              {/* One of `Assembled hh:mm`, `Regenerated hh:mm` and `Computed On Open` — the
                  API says which, and this page guesses no more than the phone does. */}
              <span className="a4__stamp" data-testid="a4-stamp">
                <b>{debriefKicker(body)}</b>
                {tournament === null ? null : ` · ${tournament.shortDisplayName}`}
              </span>
              <span className="a4__when">
                {body.day === null ? "No Matchday" : documentDateLine(body.day)}
              </span>
              {/* §2.1: the classification line must always be present. */}
              <span className="a4__class">Umpires Manager Panel · Internal</span>
            </div>
          </header>

          {/* §2.2's provenance band. Both pills are facts about this page and neither is
              broken down by author (§2.2's own rule). */}
          <div className="a4__band" data-testid="a4-band">
            <span className="a4__bandglyph" aria-hidden="true">
              ◈
            </span>
            <span className="a4__bandtext">{provenanceLine(body.ai.state)}</span>
            <span className="a4__bandpills num">
              <span className="a4pill">
                {body.matchCount === 1 ? "1 Match" : `${body.matchCount} Matches`}
              </span>
              <span className="a4pill">{notesOnPagePill(notesOnPageCount(body))}</span>
            </span>
          </div>

          {/* The API's own sentence about missing AI text, verbatim and above the two sections
              it is about — the same notice the phone prints, so a panel holding the paper and
              a panel holding the phone cannot be told the failure in two different ways. */}
          {body.ai.notice === null ? null : (
            <p className="a4note" data-testid="a4-ai-notice">
              {body.ai.notice}
            </p>
          )}

          <DocSection
            number="01"
            title="Today's Matches"
            rubric={body.day === null ? "No Matchday" : documentDateLine(body.day)}
          >
            {/* §5's rest day: the section renders and says so, never a blank. */}
            {body.fixtures.length === 0 ? (
              <p className="a4empty" data-testid="a4-no-matches">
                No Matches Today.
              </p>
            ) : (
              <ul className="fxlines">
                {body.fixtures.map((fixture) => (
                  <FixtureLine key={fixture.tmsMatchDbId} fixture={fixture} onField={[]} />
                ))}
              </ul>
            )}
          </DocSection>

          {/*
           * SECTION 02 — **spec 13 §6's per-match sections**, the spine of the restructured
           * report, and the reason spec 07's one-page rule is dropped.
           *
           * Each match is kept WHOLE across a page turn by `editorial.css`'s print rules, and
           * every `Show Notes ▾` disclosure is forced open in print by the same block: a sheet
           * handed round a room must carry its evidence rather than a row of closed triangles.
           *
           * The sections below it are the day-level half and are unchanged — spec 07's own
           * amendment is explicit that no umpire is dropped from the duty list, and §6 leaves
           * the flags, tomorrow and the closing band alone.
           */}
          <DocSection
            number="02"
            title="Matches"
            rubric={
              body.matches.length === 1
                ? "1 Match · Each Debrief In Full"
                : `${String(body.matches.length)} Matches · Each Debrief In Full`
            }
          >
            {body.matches.length === 0 ? (
              <p className="a4empty" data-testid="a4-no-match-sections">
                No Matches Today. There is no match debrief to record.
              </p>
            ) : (
              <div className="a4matches">
                {body.matches.map((section) => (
                  <A4MatchSection key={section.tmsMatchDbId} section={section} />
                ))}
              </div>
            )}
          </DocSection>

          <DocSection
            number="03"
            title="Umpires On Duty Today"
            /* §2.4's rubric claims a model wrote the lines. It is printed only when one did —
               the same rule `UmDebriefPage`'s duty kicker follows. */
            rubric={
              body.ai.state === "present"
                ? "One AI Line Per Umpire · Drawn From Released Notes Only"
                : "One Line Per Umpire · Drawn From Released Notes Only"
            }
          >
            {body.umpiresOnDuty.rows.length === 0 ? (
              <p className="a4empty" data-testid="a4-no-duty">
                Nobody Is On This List. No umpire has been appointed to this day&rsquo;s
                matches and no note about one has been released.
              </p>
            ) : (
              <>
                {body.umpiresOnDuty.source === "released_notes" ? (
                  <p className="a4empty" data-testid="a4-duty-fallback">
                    The Umpires Managers&rsquo; appointments are not held in this system for
                    this day, so this list is the umpires the day&rsquo;s released notes are
                    about — not an appointment sheet.
                  </p>
                ) : null}
                <ul className="ublks">
                  {body.umpiresOnDuty.rows.map((row) => (
                    <UmpireBlock key={row.tmsPersonId} row={row} />
                  ))}
                </ul>
              </>
            )}
          </DocSection>

          <DocSection
            number="04"
            title="Flags For Discussion"
            /* §2.5's rubric, with the count the pack actually has. `None Of Them A Judgement`
               is kept verbatim because it is true of all three kinds: every flag is computed
               in code from released notes and the schedule (`debrief/flags.ts`). */
            rubric={`${
              body.flags.length === 1 ? "1 Item" : `${String(body.flags.length)} Items`
            } · None Of Them A Judgement`}
          >
            {body.flags.length === 0 ? (
              // §5's thin day, in §5's own words.
              <p className="a4empty" data-testid="a4-no-flags">
                No Flags Tonight. Nobody is uncovered, no criterion was read twice in the same
                match, and nothing has been rising across the panel.
              </p>
            ) : (
              <ul className="a4flags">
                {body.flags.map((flag) => (
                  <FlagBlock key={flag.id} flag={flag} />
                ))}
              </ul>
            )}
          </DocSection>

          {/*
           * NOT ONE OF §2'S FOUR SECTIONS, AND HERE ON PURPOSE.
           *
           * §1 says the document is *"the same content as spec 05, laid out as the document
           * that is taken into the room"*, and spec 05 carries `Also Raised` (Q-11c). A section
           * of the pack that vanished on the printed copy would make the paper and the phone
           * two different agendas, which is the one thing a document taken into the room must
           * not be. It sits after the flags and before tomorrow, where it sits on the phone.
           */}
          <DocSection
            number="05"
            title="Also Raised"
            rubric={
              body.alsoRaised.length === 1
                ? "1 Note · About The Day Rather Than An Umpire"
                : `${String(body.alsoRaised.length)} Notes · About The Day Rather Than An Umpire`
            }
          >
            {body.alsoRaised.length === 0 ? (
              <p className="a4empty" data-testid="a4-no-also">
                Nothing was raised about the day itself — every released note is about an
                umpire.
              </p>
            ) : (
              <ul className="a4gens">
                {body.alsoRaised.map((note) => (
                  <AlsoRaisedBlock key={note.id} note={note} />
                ))}
              </ul>
            )}
          </DocSection>

          {/* §2.6 numbers this section 04. It prints as 06 because `Also Raised` is inserted
              above it in the order the phone uses, and a document whose sections were numbered
              out of sequence would be harder to talk around a table than one whose last number
              differs from the spec's. It is still the last section, which is what §2.7's
              closing band is about. */}
          <DocSection
            number="06"
            title="Tomorrow"
            rubric={
              body.tomorrow.day === null
                ? "No Next Match Day"
                : documentDateLine(body.tomorrow.day)
            }
          >
            {body.tomorrow.fixtures.length === 0 ? (
              <p className="a4empty" data-testid="a4-no-tomorrow">
                {body.tomorrow.day === null
                  ? "This is the last day of the tournament. There is nothing after it to confirm."
                  : "There are no fixtures on the next match day."}
              </p>
            ) : (
              <ul className="fxlines">
                {body.tomorrow.fixtures.map((fixture: DebriefTomorrowFixture) => (
                  <FixtureLine
                    key={fixture.tmsMatchDbId}
                    fixture={fixture}
                    onField={
                      fixture.onField.length === 0
                        ? ["Not Appointed Yet"]
                        : fixture.onField.map((entry) => entry.mediaName)
                    }
                  />
                ))}
              </ul>
            )}
          </DocSection>

          {/* §2.7's closing band. Fixed copy, and true of the meeting whatever the pack says. */}
          <div className="a4ends" data-testid="a4-ends">
            <span className="a4ends__lbl">Debrief Ends With</span>
            <span className="a4ends__what">Confirm Tomorrow&rsquo;s Pairings</span>
            <span className="a4ends__note">One Decision · Then The Room Empties</span>
          </div>

          {/* §2.8's footer rule. The schedule clause is printed only on an artefact the job
              actually assembled — see the header on why the promise is branched. */}
          <div className="a4rule" data-testid="a4-rule">
            <span className="a4rule__promise">
              {body.source === "stored"
                ? "Assembled automatically after the last match of the day, from released notes only — unreleased drafts and locked notes are never read."
                : "Assembled from released notes only — unreleased drafts and locked notes are never read."}
            </span>
            <span className="a4rule__ref num">
              {documentReference(matchdayNumber(body.days, body.day))}
            </span>
          </div>
        </article>
      )}
    </>
  );
}
